import React, { useState, useContext, useMemo, useCallback } from 'react';
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon, Person as PersonIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { PatientContext } from '../../../context/PatientContext/PatientContext';

/**
 * Componente reutilizable de búsqueda de pacientes con autocompletado.
 * Usa el PatientContext para buscar pacientes por nombre.
 *
 * @param {function} onPatientSelect - Callback cuando se selecciona un paciente.
 *                                     Recibe { memberId, memberFullName } (memberId es realmente el CPID)
 * @param {string}   label           - Label del campo (default: 'Search Patient')
 * @param {boolean}  required        - Si el campo es requerido
 * @param {string}   error           - Mensaje de error a mostrar
 * @param {object}   value           - Valor inicial { memberId, memberFullName }
 * @param {boolean}  disabled        - Si el campo está deshabilitado
 * @param {object}   sx              - Estilos MUI adicionales
 * @param {string}   size            - Tamaño del campo ('small' | 'medium')
 * @param {string}   placeholder     - Placeholder del campo
 */
export default function PatientAutocomplete({
  onPatientSelect = () => {},
  label,
  required = false,
  error = '',
  value = null,
  disabled = false,
  sx = {},
  size = 'medium',
  placeholder,
}) {
  const { t } = useTranslation();
  const { searchResults, fetchPatientsByName, loading } = useContext(PatientContext);

  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(value);
  const [open, setOpen] = useState(false);

  // Formatea los resultados para el Autocomplete
  const patientsOptions = useMemo(() => {
    if (!Array.isArray(searchResults)) return [];
    return searchResults.map((patient) => ({
      memberId: patient.memberId,
      memberFullName: patient.memberFullName,
    }));
  }, [searchResults]);

  // Debounce de búsqueda
  const handleInputChange = useCallback((event, newInputValue) => {
    setInputValue(newInputValue);
    if (newInputValue && newInputValue.length >= 3) {
      fetchPatientsByName(newInputValue);
    }
  }, [fetchPatientsByName]);

  // Cuando se selecciona un paciente
  const handleChange = (event, newValue) => {
    setSelectedValue(newValue);
    onPatientSelect(newValue);
  };

  return (
    <Autocomplete
      sx={{ ...sx }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      value={selectedValue}
      onChange={handleChange}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={patientsOptions}
      loading={loading}
      disabled={disabled}
      getOptionLabel={(option) => {
        if (typeof option === 'string') return option;
        return option?.memberFullName || '';
      }}
      isOptionEqualToValue={(option, val) => option?.memberId === val?.memberId}
      filterOptions={(x) => x} // No filtrar localmente, la API ya filtra
      noOptionsText={
        inputValue.length < 3
          ? t('typeToSearch', 'Type at least 3 characters to search')
          : t('patientNotFound', 'No patients found')
      }
      renderOption={(props, option) => (
        <li {...props} key={option.memberId}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 0.5 }}>
            <PersonIcon color="action" fontSize="small" />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {option.memberFullName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                ID: {option.memberId}
              </Typography>
            </Box>
          </Box>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label || t('searchPatient', 'Search Patient')}
          placeholder={placeholder || t('typePatientName', 'Type patient name...')}
          required={required}
          error={!!error}
          helperText={error}
          size={size}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
